import { useEffect, useMemo, useState, type ReactNode } from "react"
import { toast } from "sonner"
import { type CurrentUser, api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Download, FileSpreadsheet, RotateCcw } from "lucide-react"
import { syncVpnClients } from "../lib/vpn-client-sync"

type Filter = "all" | "active" | "inactive"

type Props = {
  machines: string[]
  currentUser: CurrentUser
}

export function ExportsPage({ machines, currentUser }: Props) {
  const [filter, setFilter] = useState<Filter>("all")
  const [selectedMachines, setSelectedMachines] = useState<Set<string>>(
    () => new Set(machines)
  )
  const [exporting, setExporting] = useState(false)

  const canView = currentUser.permissions.includes("view")

  useEffect(() => {
    setSelectedMachines((current) => {
      const available = new Set(machines)
      const kept = [...current].filter((machine) => available.has(machine))
      if (kept.length === 0) return new Set(machines)
      return new Set(kept)
    })
  }, [machines])

  const selectedCount = selectedMachines.size
  const allSelected = machines.length > 0 && selectedCount === machines.length
  const selectedMachineList = useMemo(
    () => machines.filter((machine) => selectedMachines.has(machine)),
    [machines, selectedMachines]
  )

  const toggleMachine = (machine: string) => {
    setSelectedMachines((current) => {
      const next = new Set(current)
      if (next.has(machine)) next.delete(machine)
      else next.add(machine)
      return next
    })
  }

  const handleExport = async () => {
    if (selectedMachineList.length === 0) {
      toast.error("Selecione pelo menos um ambiente.")
      return
    }

    setExporting(true)
    try {
      await Promise.all(
        selectedMachineList.map((machineName) => syncVpnClients(machineName))
      )
      await api.exportAllClientsWorkbookDownload(filter, selectedMachineList)
      toast.success("Excel exportado.")
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao exportar.")
    } finally {
      setExporting(false)
    }
  }

  if (!canView) {
    return (
      <div className="flex w-full flex-1 flex-col items-start justify-start p-4 sm:p-6 md:p-8">
        <p className="text-sm text-muted-foreground">
          Sem permissão para acessar esta seção.
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-full min-w-0 flex-1 flex-col gap-6 p-4 sm:p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Exportações</h1>
          <p className="text-sm text-muted-foreground">
            Arquivos consolidados para conferência dos usuários VPN.
          </p>
        </div>
        <Button
          size="sm"
          onClick={handleExport}
          disabled={exporting || selectedMachineList.length === 0}
        >
          <Download className={exporting ? "animate-pulse" : ""} />
          Exportar Excel
        </Button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1.1fr)_minmax(280px,0.9fr)]">
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileSpreadsheet className="size-4" />
                Excel por ambiente
              </CardTitle>
              <Badge variant="secondary" className="font-mono text-[10px]">
                {selectedCount} selecionado(s)
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <LabelText>Filtro de usuários</LabelText>
              <div className="flex flex-wrap gap-2">
                {(["all", "active", "inactive"] as Filter[]).map((option) => (
                  <Button
                    key={option}
                    type="button"
                    size="sm"
                    variant={filter === option ? "default" : "outline"}
                    onClick={() => setFilter(option)}
                  >
                    {option === "all"
                      ? "Todos"
                      : option === "active"
                        ? "Ativos"
                        : "Inativos"}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <LabelText>Ambientes</LabelText>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => setSelectedMachines(new Set(machines))}
                    disabled={machines.length === 0 || allSelected}
                  >
                    Todos
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs"
                    onClick={() => setSelectedMachines(new Set())}
                    disabled={selectedCount === 0}
                  >
                    Limpar
                  </Button>
                </div>
              </div>

              {machines.length === 0 ? (
                <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
                  Nenhum ambiente cadastrado.
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {machines.map((machine) => (
                    <div
                      key={machine}
                      className="flex min-h-11 cursor-pointer items-center gap-3 rounded-md border bg-background px-3 py-2 text-left text-sm transition-colors hover:bg-muted/40"
                      onClick={() => toggleMachine(machine)}
                    >
                      <Checkbox
                        checked={selectedMachines.has(machine)}
                        onCheckedChange={() => toggleMachine(machine)}
                        onClick={(event) => event.stopPropagation()}
                        aria-label={`Selecionar ${machine}`}
                      />
                      <span className="min-w-0 flex-1 truncate">{machine}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Resumo</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <SummaryItem label="Ambientes" value={String(selectedCount)} />
              <SummaryItem
                label="Filtro"
                value={
                  filter === "all"
                    ? "Todos"
                    : filter === "active"
                      ? "Ativos"
                      : "Inativos"
                }
              />
            </div>

            <div className="space-y-2">
              <LabelText>Planilhas no arquivo</LabelText>
              <div className="max-h-72 overflow-y-auto rounded-md border">
                {selectedMachineList.length === 0 ? (
                  <div className="p-3 text-sm text-muted-foreground">
                    Nenhum ambiente selecionado.
                  </div>
                ) : (
                  selectedMachineList.map((machine) => (
                    <div
                      key={machine}
                      className="border-b px-3 py-2 text-sm last:border-0"
                    >
                      <span className="block truncate">{machine}</span>
                    </div>
                  ))
                )}
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                setFilter("all")
                setSelectedMachines(new Set(machines))
              }}
            >
              <RotateCcw />
              Restaurar padrão
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function LabelText({ children }: { children: ReactNode }) {
  return (
    <div className="text-xs font-medium uppercase tracking-normal text-muted-foreground">
      {children}
    </div>
  )
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 truncate text-sm font-medium">{value}</div>
    </div>
  )
}
